import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  createAutomation,
  getAutomationById,
  updateAutomation,
} from "./services/AutomationServices";
import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";

// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from '../AgentHub/contexts/AgentHubContext';
import { IconArrowLeft, IconDeviceFloppy, IconCheck, IconX } from "@tabler/icons-react";
import styled from "styled-components";

// Schema de validação
const schema = yup
  .object({
    name: yup.string().required("Nome é obrigatório"),
    automation_type: yup.string().required("Tipo de automação é obrigatório"),
    followup_ai_prompt: yup.string().when("automation_type", {
      is: "followup",
      then: (schema) => schema.required("Prompt de IA é obrigatório para follow-up automático"),
      otherwise: (schema) => schema.notRequired(),
    }),
  })
  .required();

const AutomationForm = () => {
  const { id, agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  // Tentar obter contexto do AgentHub (opcional)
  const agentContext = useAgentHubContext();
  const contextAgentId = agentContext?.agentId || agentId;
  // Detectar se está no AgentHub pela URL ou contexto
  const isInsideAgentHub = Boolean(agentContext) || window.location.pathname.includes('/agent-hub/');
  
  // Debug do contexto
  console.log('=== DEBUG CONTEXTO AutomationForm ===');
  console.log('agentContext:', agentContext);
  console.log('agentId (from params):', agentId);
  console.log('contextAgentId:', contextAgentId);
  console.log('isInsideAgentHub:', isInsideAgentHub);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      automation_type: "whatsapp",
      active: true,
      followup_ai_prompt:
        "Analise o histórico da conversa e gere insights sobre o cliente.",
    },
  });

  const isActive = watch("active");
  const automationType = watch("automation_type");

  useEffect(() => {
    if (id) {
      setInitialLoading(true);
      getAutomationById(id)
        .then((data) => {
          reset({
            name: data.name,
            description: data.description,
            automation_type: data.automation_type || "whatsapp",
            active: data.active,
            followup_ai_prompt:
              data.followup_ai_prompt ||
              "Analise o histórico da conversa e gere insights sobre o cliente.",
          });
        })
        .catch((error) => {
          console.error("Erro ao carregar automação:", error);
          setNotify({
            title: "Erro!",
            message: "Ocorreu um erro ao carregar a automação.",
            type: "error",
            visible: true,
          });
          if (isInsideAgentHub) {
            navigate(`/agent-hub/${contextAgentId}/automations`);
          } else {
            navigate("/automations");
          }
        })
        .finally(() => {
          setInitialLoading(false);
        });
    }
  }, [id, reset, navigate, setNotify]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const automationData = {
        ...data,
        account_type: "generic",
        account_id: user.account_id,
        user: user.user_id,
        ...(contextAgentId && { agent: contextAgentId }),
      };

      if (id) {
        await updateAutomation(id, automationData);
        Swal.fire({
          title: "Sucesso!",
          text: "Automação atualizada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        if (isInsideAgentHub) {
          navigate(`/agent-hub/${contextAgentId}/automations`);
        } else {
          navigate("/automations");
        }
      } else {
        const response = await createAutomation(automationData);
        const newAutomationId = response.id;

        Swal.fire({
          title: "Sucesso!",
          text: "Automação criada com sucesso!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          Swal.fire({
            title: "Adicionar Regras",
            text: "Deseja adicionar regras a esta automação agora?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
          }).then((result) => {
            console.log('=== DEBUG NAVEGAÇÃO ===');
            console.log('isInsideAgentHub:', isInsideAgentHub);
            console.log('contextAgentId:', contextAgentId);
            console.log('newAutomationId:', newAutomationId);
            
            if (result.isConfirmed) {
              if (isInsideAgentHub) {
                const url = `/agent-hub/${contextAgentId}/automations/${newAutomationId}`;
                console.log('Navegando para (AgentHub):', url);
                navigate(url);
              } else {
                const url = `/automations/${newAutomationId}`;
                console.log('Navegando para (Standalone):', url);
                navigate(url);
              }
            } else {
              if (isInsideAgentHub) {
                const url = `/agent-hub/${contextAgentId}/automations`;
                console.log('Navegando para lista (AgentHub):', url);
                navigate(url);
              } else {
                const url = "/automations";
                console.log('Navegando para lista (Standalone):', url);
                navigate(url);
              }
            }
          });
        });
      }
    } catch (error) {
      console.error("Erro ao salvar automação:", error);

      let errorMessage = "Ocorreu um erro ao salvar a automação.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorDetails = Object.entries(errorData)
          .map(
            ([field, messages]) =>
              `${field}: ${
                Array.isArray(messages) ? messages.join(", ") : messages
              }`
          )
          .join("; ");

        if (errorDetails) {
          errorMessage += ` Detalhes: ${errorDetails}`;
        }
      }

      setNotify({
        title: "Erro!",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };


  if (initialLoading) {
    return (
      <>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Carregando informações da automação...</LoadingText>
        </LoadingContainer>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <Header>
        <HeaderContent>
          <BackButton onClick={() => {
            if (isInsideAgentHub) {
              navigate(`/agent-hub/${contextAgentId}/automations`);
            } else {
              navigate('/automations');
            }
          }}>
            <IconArrowLeft size={20} />
          </BackButton>
          <HeaderInfo>
            <Title>{id ? "Editar Automação" : "Nova Automação"}</Title>
            <Subtitle>
              {id
                ? "Atualize as informações da automação."
                : "Defina as configurações iniciais da automação."}
            </Subtitle>
          </HeaderInfo>
        </HeaderContent>
      </Header>

      <FormCard>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information */}
          <FormSection>
            <SectionTitle>Informações Básicas</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label>
                  Nome da Automação <RequiredIndicator>*</RequiredIndicator>
                </Label>
                <Input
                  type="text"
                  placeholder="Ex: Automação de Consultas Médicas"
                  {...register("name")}
                  error={!!errors.name}
                />
                {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
              </FormGroup>

              <FormGroup>
                <Label>
                  Tipo de Automação <RequiredIndicator>*</RequiredIndicator>
                </Label>
                <Select
                  {...register("automation_type")}
                  error={!!errors.automation_type}
                >
                  <option value="whatsapp">📱 WhatsApp</option>
                  <option value="email">📧 Email</option>
                  <option value="webhook">🔗 Webhook</option>
                  <option value="followup">🤖 Follow-up Automático</option>
                </Select>
                {errors.automation_type && <ErrorText>{errors.automation_type.message}</ErrorText>}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Descrição</Label>
              <TextArea
                rows={3}
                placeholder="Descreva o propósito desta automação..."
                {...register("description")}
              />
              <HelpText>Opcional: Uma descrição clara ajuda na organização das automações.</HelpText>
            </FormGroup>

            {/* Campo específico para follow-up automático */}
            {automationType === 'followup' && (
              <FormGroup>
                <Label>
                  Prompt para IA <RequiredIndicator>*</RequiredIndicator>
                </Label>
                <TextArea
                  rows={8}
                  placeholder="Digite o prompt que guiará a IA para gerar mensagens de follow-up..."
                  {...register("followup_ai_prompt")}
                  error={!!errors.followup_ai_prompt}
                />
                {errors.followup_ai_prompt && <ErrorText>{errors.followup_ai_prompt.message}</ErrorText>}
                <HelpText>Este prompt guiará a IA para analisar conversas e gerar mensagens personalizadas de follow-up.</HelpText>
              </FormGroup>
            )}
          </FormSection>


          {/* Status */}
          <FormSection>
            <StatusContainer>
              <StatusToggle>
                <ToggleContainer>
                  <ToggleInput
                    type="checkbox"
                    id="active-toggle"
                    {...register("active")}
                  />
                  <ToggleSlider htmlFor="active-toggle" active={isActive}>
                    <ToggleKnob active={isActive} />
                  </ToggleSlider>
                </ToggleContainer>
                <StatusInfo>
                  <StatusLabel>
                    {isActive ? (
                      <>
                        <IconCheck size={18} />
                        Automação Ativa
                      </>
                    ) : (
                      <>
                        <IconX size={18} />
                        Automação Inativa
                      </>
                    )}
                  </StatusLabel>
                  <StatusDescription>
                    {isActive 
                      ? "As regras desta automação serão executadas automaticamente."
                      : "Nenhuma mensagem será enviada automaticamente."
                    }
                  </StatusDescription>
                </StatusInfo>
              </StatusToggle>
            </StatusContainer>
          </FormSection>

          {/* Actions */}
          <ActionButtons>
            <SecondaryButton type="button" onClick={() => {
              if (isInsideAgentHub) {
                navigate(`/agent-hub/${contextAgentId}/automations`);
              } else {
                navigate("/automations");
              }
            }}>
              Cancelar
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner small />
                  Salvando...
                </>
              ) : (
                <>
                  <IconDeviceFloppy size={18} />
                  {id ? 'Atualizar' : 'Criar'} Automação
                </>
              )}
            </PrimaryButton>
          </ActionButtons>
        </Form>
      </FormCard>
    </>
  );
};



const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: ${props => props.small ? '20px' : '40px'};
  height: ${props => props.small ? '20px' : '40px'};
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
`;

const Header = styled.div`
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 24px;
    border-radius: 12px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;


const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RequiredIndicator = styled.span`
  color: var(--danger);
  font-size: 16px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid ${props => props.error ? 'var(--danger)' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;


const StatusContainer = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
`;

const StatusToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ToggleContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  position: absolute;
`;

const ToggleSlider = styled.label`
  display: block;
  width: 52px;
  height: 28px;
  background: ${props => props.active ? 'var(--primary)' : 'var(--border-color)'};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
`;

const ToggleKnob = styled.div`
  width: 22px;
  height: 22px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 3px;
  left: ${props => props.active ? '27px' : '3px'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusLabel = styled.h4`
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const StatusDescription = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  margin: 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ErrorText = styled.span`
  color: var(--danger);
  font-size: 12px;
  font-weight: 500;
`;

const HelpText = styled.span`
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.3;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SecondaryButton = styled.button`
  background: var(--surface);
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    justify-content: center;
  }
`;

export default AutomationForm;